import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { money } from "@/components/LoadMoney";
import { finance, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { DownloadSimple, GasPump, RoadHorizon } from "@phosphor-icons/react";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Reports() {
  const { user } = useAuth();
  const isOwner = user?.fleet_role === "owner";
  const [month, setMonth] = useState(thisMonth());
  const [rep, setRep] = useState(null);
  useEffect(() => {
    if (!isOwner || !month) { return; }
    let stale = false;
    setRep(null);
    finance.report(month)
      .then((r) => { if (!stale) setRep(r); })
      .catch((e) => { if (stale) return; toast.error(formatErr(e)); setRep({ error: true }); });
    return () => { stale = true; };
  }, [month, isOwner]);
  if (!isOwner) return <Navigate to="/app" replace/>;

  return <AppShell>
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <div className="eyebrow text-accent">Fleet owner</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Profit reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Pick a month, check the numbers, then download a spreadsheet for taxes or your accountant.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="eyebrow block mb-1">Month</span>
          <input type="month" data-testid="report-month" value={month} max={thisMonth()} onChange={(e) => setMonth(e.target.value)}
            className="border-2 border-border focus:border-accent px-3 py-2 outline-none bg-white"/>
        </label>
        <DownloadButton month={month} disabled={!rep?.loads?.length}/>
      </div>
    </div>
    <ReportBody rep={rep}/>
  </AppShell>;
}

function DownloadButton({ month, disabled }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await finance.exportCsv(month);
      const url = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), { href: url, download: `haulguard-profit-${month}.csv` });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Spreadsheet downloaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return <button data-testid="btn-download-csv" onClick={download} disabled={busy || disabled} className="btn-amber disabled:opacity-50">
    <DownloadSimple size={18} weight="bold"/> {busy ? "Preparing…" : "Download CSV"}
  </button>;
}

function ReportBody({ rep }) {
  if (!rep) return <div className="text-muted-foreground" data-testid="report-loading">Loading…</div>;
  if (rep.error) return <div className="border-2 border-dashed p-10 text-center text-muted-foreground">Couldn't load this report.</div>;
  return <>
    <Summary s={rep.summary} label={rep.label}/>
    {rep.loads.length === 0
      ? <div className="border-2 border-dashed p-10 text-center text-muted-foreground" data-testid="report-empty">No loads picked up in {rep.label}.</div>
      : <div className="space-y-3" data-testid="report-loads">{rep.loads.map(ld => <LoadBlock key={ld.id} ld={ld}/>)}</div>}
    <p className="text-xs text-muted-foreground mt-6">Loads count toward the month of their pickup date (or the day they were created if no pickup date is set).</p>
  </>;
}

function Summary({ s, label }) {
  const cells = [["Loads", s.loads, "sum-loads"], ["Revenue", money(s.revenue), "sum-revenue"], ["Fuel", money(s.fuel), "sum-fuel"],
    ["Tolls", money(s.tolls), "sum-tolls"], ["Loaded miles", s.loaded_miles ? s.loaded_miles.toLocaleString() : "—", "sum-miles"],
    ["Profit / mile", s.profit_per_mile == null ? "—" : money(s.profit_per_mile), "sum-ppm"]];
  return <section className="bg-primary text-primary-foreground p-5 border-l-8 border-accent mb-6" data-testid="report-summary">
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
      <h2 className="font-display font-bold text-xl">{label}</h2>
      <div data-testid="sum-profit"><span className="eyebrow opacity-70 mr-2">Profit</span><span className="font-display font-black text-3xl text-accent">{money(s.profit)}</span></div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
      {cells.map(([k, v, tid]) => <div key={k} data-testid={tid}><div className="eyebrow opacity-70">{k}</div><div className="font-display font-bold text-xl">{v}</div></div>)}
    </div>
  </section>;
}

function LoadBlock({ ld }) {
  const f = ld.finance;
  return <div className="bg-white border border-border/60" data-testid={`report-load-${ld.id}`}>
    <Link to={`/app/loads/${ld.id}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 p-4 border-b border-border/60 hover:bg-secondary transition-colors">
      <div className="flex-1 min-w-[12rem]">
        <div className="font-mono-tech text-xs text-muted-foreground">#{ld.load_number} · {ld.date} · {ld.added_by}</div>
        <div className="font-display font-bold">{ld.broker || "No broker"} <span className="font-normal text-muted-foreground text-sm">{ld.pickup_city || "—"} → {ld.delivery_city || "—"}</span></div>
      </div>
      <Fig k="Revenue" v={money(f.revenue)}/><Fig k="Costs" v={money(f.costs)}/>
      <Fig k="Miles" v={f.loaded_miles ? f.loaded_miles.toLocaleString() : "—"}/>
      <Fig k="Profit" v={money(f.profit)} strong neg={f.profit < 0} tid={`report-profit-${ld.id}`}/>
    </Link>
    <div className="grid md:grid-cols-2 gap-4 p-4 text-sm">
      <div>
        <div className="eyebrow text-muted-foreground mb-1">Vehicles</div>
        {ld.vehicles.length === 0 && <div className="text-muted-foreground">None</div>}
        {ld.vehicles.map(v => <div key={v.id} className="flex justify-between gap-2 py-0.5"><span className="truncate">{v.year} {v.make} {v.model} <span className="font-mono-tech text-[11px] text-muted-foreground">{v.vin}</span></span><span className="font-mono-tech">{money(v.rate)}</span></div>)}
      </div>
      <div>
        <div className="eyebrow text-muted-foreground mb-1">Receipts</div>
        {ld.receipts.length === 0 && <div className="text-muted-foreground">None</div>}
        {ld.receipts.map(r => {
          const Icon = r.kind === "fuel" ? GasPump : RoadHorizon;
          return <div key={r.id} className="flex justify-between gap-2 py-0.5"><span className="truncate"><Icon size={14} className="inline mr-1"/>{r.date} · <span className="capitalize">{r.kind}</span>{r.note ? ` · ${r.note}` : ""}</span><span className="font-mono-tech">{money(r.amount)}</span></div>;
        })}
      </div>
    </div>
  </div>;
}

function Fig({ k, v, strong, neg, tid }) {
  return <div className="text-right" data-testid={tid}>
    <div className="eyebrow text-muted-foreground">{k}</div>
    <div className={`font-display ${strong ? "font-black text-lg" : "font-bold"} ${neg ? "text-red-600" : ""}`}>{v}</div>
  </div>;
}
